'use client';

import styles from './Whitepaper.module.css';
import { Container, CustomImage } from 'components/shared';
import CustomLink from 'components/shared/CustomLink';
import { useEffect, useCallback, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { CaretUp } from '../../assets/icons';

interface WhitepaperData {
  title: string;
  description: string | null;
  link: string;
  imageSrc: string;
  imageAlt: string;
}

const WhitepaperData: WhitepaperData[] = [
  {
    title:
      "Hormonal surge concordance of the Ultrahuman's combinatorial cycle tracking algorithm and predicted fertile window: 'Cycle & Ovulation PowerPlug' Pilot Study",
    description: null,
    link: 'https://science.ultrahuman.com/studies/fertile-window-algorithm',
    imageSrc: 'web_v2/womens-health/whitepapers/pilot_study.png',
    imageAlt: 'Pilot Study',
  },
  {
    title:
      'Impact of BMI, stress, and activity on menstrual cycle length and variability: insights from 4055 cycles of Ultrahuman Ring AIR users',
    description: null,
    link: 'https://science.ultrahuman.com/studies/impact-of-bmi-stress-and-activity-on-menstrual-cycle-length-and-variability',
    imageSrc: 'web_v2/womens-health/whitepapers/bmi_stress_activity.png',
    imageAlt: 'BMI Stress Activity Study',
  },
  {
    title:
      'How does heart rate and heart rate variability change over the day in men and women? A database analysis of Ultrahuman Ring AIR users',
    description: null,
    link: 'https://science.ultrahuman.com/studies/is-stress-in-women-more-circadian-aligned',
    imageSrc: 'web_v2/womens-health/whitepapers/hr_hrv.png',
    imageAlt: 'HR HRV Study',
  },
  {
    title:
      'Modifying sleep tracking algorithms reveals specific sleep deprivation patterns in women: early findings from the Ultrahuman Ring AIR Ramadan Mode',
    description: null,
    link: 'https://science.ultrahuman.com/studies/ramadan-mode-sleep-debt-in-women',
    imageSrc: 'web_v2/womens-health/whitepapers/sleep_ramadan.png',
    imageAlt: 'Sleep Ramadan Study',
  },
  {
    title:
      'Temperature-Derived Cycle Flags: Characterizing Menstrual Cycle Dynamics Using Ultrahuman Cycle Tracking & Ovulation (C&O Pro) Algorithm powered by OvuSense™',
    description: null,
    link: 'https://science.ultrahuman.com/studies/cycle-flags',
    imageSrc: 'web_v2/womens-health/whitepapers/cnopro_flag.png',
    imageAlt: 'CNO Pro Flag Study',
  },
  {
    title:
      'Listening In: Introducing the Ultrahuman Cycle Tracking & Ovulation Pro (C&O Pro) Feature Powered by OvuSense™',
    description: null,
    link: 'https://science.ultrahuman.com/studies/cycle-tracking-pro-accuracy',
    imageSrc: 'web_v2/womens-health/whitepapers/cno_pro.png',
    imageAlt: 'CNO Pro Study',
  },
];

interface WhitePaperCardProps {
  data: WhitepaperData;
}

const WhitePaperCard: React.FC<WhitePaperCardProps> = ({ data }) => {
  return (
    <article className={styles.whitePaperArticle}>
      <div className={styles.imgContainer}>
        <CustomImage
          src={data.imageSrc}
          alt={data.imageAlt}
          width={1800}
          height={1948}
        />
      </div>
      <div className={styles.text}>
        <div>
          <h3>{data.title}</h3>
          {data.description && <p> {data.description}</p>}
        </div>
        <CustomLink href={data.link}>Read the study</CustomLink>
      </div>
    </article>
  );
};

export const WhitepaperSection = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: 'start',
    slidesToScroll: 1,
  });

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const handleSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };

    handleSelect();
    emblaApi.on('select', handleSelect);

    return () => {
      emblaApi.off('select', handleSelect);
    };
  }, [emblaApi]);

  return (
    <div className={styles.whitepaperSectionContainer}>
      <Container>
        <h3 className={styles.caption}>SCIENCE</h3>
        <h2 className={styles.title}>
          Backed by research. Built for your cycle.
        </h2>
        <div className={styles.carouselContainer}>
          <div className={styles.embla} ref={emblaRef}>
            <div className={styles.embla__container}>
              {WhitepaperData.map((whitepaper, index) => (
                <div className={styles.embla__slide} key={index}>
                  <WhitePaperCard data={whitepaper} />
                </div>
              ))}
            </div>
          </div>
          <div className={styles.carouselControls}>
            <button
              className={styles.embla__prev}
              onClick={scrollPrev}
              disabled={!canScrollPrev}
            >
              <CaretUp style={{ transform: 'rotate(-90deg)' }} />
            </button>
            <button
              className={styles.embla__next}
              onClick={scrollNext}
              disabled={!canScrollNext}
            >
              <CaretUp style={{ transform: 'rotate(90deg)' }} />
            </button>
          </div>
        </div>
      </Container>
    </div>
  );
};
